import React, { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import {
  usePatchCollaboratorMutation,
  usePatchProfileMutation,
  useUploadProfileImageFormDataMutation,
} from "../../redux/appSlices/collaborator/collaboratorApiSlice";
import { getProfilePicturePatchTarget } from "../../utils/profilePicture";
import { translation } from "../../translation";
import { toast } from "react-toastify";
import {
  AiOutlineDelete,
  AiOutlineEdit,
  AiOutlinePlusCircle,
} from "react-icons/ai";
import CropProfilePic from "../profile/CropProfilePic";

const ProfilePicture = ({ edit, setOpenEditPopup, setFinalStep }) => {
  const t = translation.onboarding.profilePicture;
  const lang = useSelector(
    (state) => state.collaborator.collaboratorInfo.language,
  );
  const collaborator = useSelector(
    (state) => state.collaborator.collaboratorInfo,
  );

  const [patchCollaborator, { isLoading: patchLoading }] =
    usePatchCollaboratorMutation();
  const [patchProfile] = usePatchProfileMutation();
  const [uploadProfileImageFormData, { isLoading: uploadLoading }] =
    useUploadProfileImageFormDataMutation();
  const fileInputRef = useRef(null);

  const [previewUrl, setPreviewUrl] = useState(
    collaborator?.profile_picture || null,
  );
  const [cropFile, setCropFile] = useState(null);
  const [cropBusy, setCropBusy] = useState(false);

  useEffect(() => {
    setPreviewUrl(collaborator?.profile_picture || null);
  }, [collaborator?.profile_picture]);

  useEffect(() => {
    if (cropFile) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "visible";
    }
    return () => {
      document.body.style.overflow = "visible";
    };
  }, [cropFile]);

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setCropFile(file);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const saveProfilePicture = async (profilePictureUrl) => {
    const patchTarget = getProfilePicturePatchTarget(collaborator);
    if (!patchTarget) return;

    if (patchTarget.type === "profile") {
      await patchProfile({
        id: patchTarget.id,
        content: { profile_picture: profilePictureUrl },
      }).unwrap();
    } else {
      await patchCollaborator({
        id: patchTarget.id,
        content: { profile_picture: profilePictureUrl },
      }).unwrap();
    }
  };

  const handleCroppedProfilePictureUpload = async (file) => {
    const res = await uploadProfileImageFormData(file).unwrap();
    const url =
      res?.data?.profile_picture ||
      res?.data?.imagePath ||
      res?.data?.collaborator?.profile_picture;

    if (url) {
      setPreviewUrl(url);
    }
  };

  const handleDeletePhoto = () => {
    setPreviewUrl(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }

    saveProfilePicture("")
      .catch(() =>
        toast.error(
          edit
            ? translation?.updateErrorMessage?.[lang]
            : translation?.saveErrorMessage?.[lang],
        ),
      );
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!edit) {
      setFinalStep?.(true);
    }
    patchCollaborator({
      id: collaborator._id,
      content: {
        ...(edit ? {} : { onboarding_step: 11 }),
      },
    })
      .unwrap()
      .then(() => {
        if (edit && setOpenEditPopup) setOpenEditPopup(false);
      })
      .catch(() =>
        toast.error(
          edit
            ? translation?.updateErrorMessage?.[lang]
            : translation?.saveErrorMessage?.[lang],
        ),
      );
  };

  const loading = patchLoading || uploadLoading || cropBusy;

  return (
    <section
      className={`pt-[7vh] mx-5 pb-[120px] ${
        lang === "ar" ? "font-readexRegular" : "font-SteradianRegular"
      }`}
    >
      <div>
        <div
          className={`text-2xl ${
            lang === "ar" ? "font-lalezarRegular" : "font-SteradianMedium"
          }`}
        >
          {t.title[lang]}
        </div>
        <div className="text-lg mt-5">{t.subtitle[lang]}</div>

        <div className="flex flex-col items-center mt-8">
          <div className="relative">
            {previewUrl ? (
              <div className="size-40 shrink-0 overflow-hidden rounded-full">
                <img
                  className="size-full object-cover rounded-full"
                  src={`${process.env.REACT_APP_IMAGE_PATH}${previewUrl}`}
                  alt=""
                />
              </div>
            ) : (
              <div className="flex size-40 shrink-0 items-center justify-center rounded-full bg-labelsBg border-2 border-dashed border-inputBottom">
                <span className="text-labels text-sm text-center px-2">
                  {t.placeholder[lang]}
                </span>
              </div>
            )}
            {(uploadLoading || cropBusy) && (
              <div className="absolute left-0 top-0 z-30 flex size-40 items-center justify-center rounded-full border-2 border-dashed border-inputBottom bg-white">
                <img
                  src="/assets/img/loaders/loading.gif"
                  alt=""
                  className="w-[50px] h-[50px]"
                />
              </div>
            )}
          </div>

          {previewUrl ? (
            <div className="flex flex-col gap-3 w-full max-w-sm mt-6">
              <label
                htmlFor="onboarding-profile-picture"
                className="w-full h-14 border-dashed border items-center justify-center border-labels bg-labelsBg flex flex-row gap-2 rounded-xl text-labels cursor-pointer"
              >
                <AiOutlineEdit className="text-xl shrink-0" />
                <span>{t.changePhoto[lang]}</span>
              </label>
              <button
                type="button"
                onClick={handleDeletePhoto}
                disabled={patchLoading || uploadLoading}
                className="w-full h-14 border-dashed border items-center justify-center border-red-300/80 bg-popcard flex flex-row gap-2 rounded-xl text-red-600 cursor-pointer disabled:opacity-50"
              >
                <AiOutlineDelete className="text-xl shrink-0" />
                <span>{t.deletePhoto[lang]}</span>
              </button>
            </div>
          ) : (
            <label
              htmlFor="onboarding-profile-picture"
              className="w-full max-w-sm h-14 border-dashed border items-center justify-center border-labels bg-labelsBg flex flex-row gap-2 rounded-xl mt-6 text-labels cursor-pointer"
            >
              <AiOutlinePlusCircle className="text-xl shrink-0" />
              <span>{t.uploadCta[lang]}</span>
            </label>
          )}
          <input
            ref={fileInputRef}
            id="onboarding-profile-picture"
            type="file"
            accept="image/*"
            className="hidden"
            onChange={handleFileSelect}
          />
        </div>
      </div>

      {cropFile && (
        <div className="fixed inset-0 z-[99999] bg-popcard animate-slide overflow-x-auto pb-80">
          <div className="max-w-xl m-auto min-h-[100vh]">
            <div
              className={`cursor-pointer flex justify-end mt-9 ${
                lang === "ar" ? "ml-5" : "mr-5"
              }`}
              onClick={() => setCropFile(null)}
            >
              <img src="../../assets/img/icons/close.svg" alt="" />
            </div>
            <CropProfilePic
              imageSrc={cropFile}
              setOpenEditPopup={() => setCropFile(null)}
              setOpenPopup={() => {}}
              setProfileLoading={setCropBusy}
              onCroppedUpload={handleCroppedProfilePictureUpload}
              cropAspectRatio={1}
            />
          </div>
        </div>
      )}

      <div className="button-container">
        <button
          className={`btn-fields btn-active ${lang === "ar" ? "!font-readexRegular" : ""}`}
          id="btn-next-profile-picture"
          type="button"
          onClick={handleSubmit}
          disabled={loading}
        >
          {loading ? (
            <img
              src="/assets/img/loaders/loading.gif"
              style={{ padding: "2px" }}
              width="23"
              alt=""
              className="mx-auto"
            />
          ) : edit ? (
            t.buttonSave[lang]
          ) : (
            t.buttonNext[lang]
          )}
        </button>
      </div>
    </section>
  );
};

export default ProfilePicture;
